import { css } from "@linaria/core";
import { useMutation } from "@apollo/client/react";
import { MailOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Flex, Form, Input, Typography } from "antd";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";
import { RequestEmailLinkDocument } from "../../gql/generated/graphqlSchema.tsx";
import { LoadingBlocker } from "../../components/LoadingBlocker.tsx";
import { useServiceError } from "../../hooks/useServiceError.ts";
import { LanguageSwitcher } from "../../components/LanguageSwitcher.tsx";
import LogoSvg from "../../assets/logo.svg?react";
import { routes } from "../../routes.ts";

const { Title, Text } = Typography;

const page = css`
  min-height: 100vh;
  background: var(--ant-color-bg-layout);
`;

const logo = css`
  display: block;
  width: 64px;
  height: 64px;
  margin: 0 auto var(--ant-margin-sm);
  border-radius: var(--ant-border-radius-lg);
`;

const productName = css`
  text-align: center;
  margin-top: 0;
  margin-bottom: var(--ant-margin-lg);
`;

const brandLink = css`
  display: block;
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus {
    color: inherit;
    text-decoration: none;
  }
`;

const langSwitcher = css`
  margin-top: var(--ant-margin-sm);
`;

const card = css`
  width: 100%;
  max-width: 420px;
  box-shadow: var(--ant-box-shadow-secondary);
`;

const titleStyle = css`
  text-align: center;
  margin-top: 0;
  margin-bottom: var(--ant-margin-xxs);
`;

const subtitle = css`
  display: block;
  text-align: center;
  margin-bottom: var(--ant-margin-md);
`;

const alert = css`
  margin-bottom: var(--ant-margin);
`;

const submit = css`
  margin-bottom: var(--ant-margin-sm);
`;

const sentEmphasis = css`
  color: var(--ant-color-link);
`;

const invitationNote = css`
  display: block;
  margin-bottom: var(--ant-margin);
  padding: var(--ant-padding-sm) var(--ant-padding);
  background: var(--ant-color-info-bg);
  border: var(--ant-line-width) solid var(--ant-color-info-border);
  border-radius: var(--ant-border-radius);
  color: var(--ant-color-text);
  font-size: var(--ant-font-size-sm);
  line-height: var(--ant-line-height);
`;

const consentLine = css`
  display: block;
  text-align: center;
  margin-top: 0;
  font-size: var(--ant-font-size-sm);
  color: var(--ant-color-text-secondary);

  & a {
    color: var(--ant-color-link);
  }
`;

type FormValues = { email: string };

export const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);
  const [requestEmailLink, { loading, error }] = useMutation(RequestEmailLinkDocument);

  const errorMessage = useServiceError(error);

  const onFinish = async (values: FormValues) => {
    const trimmed = values.email.trim();
    const result = await requestEmailLink({
      variables: {
        input: { email: trimmed, locale: i18n.language.split("-")[0] ?? "en" },
      },
    });
    if (result.data?.requestEmailLink) {
      setSentToEmail(trimmed);
    }
  };

  return (
    <Flex align="center" className={page} justify="center" vertical>
      <LoadingBlocker loading={loading} />
      <Card className={card}>
        <Link className={brandLink} to={routes.landing.to()}>
          <LogoSvg className={logo} />
          <Title className={productName} level={3}>
            CALCULABEL
          </Title>
        </Link>
        {sentToEmail ? (
          <>
            <Title className={titleStyle} level={3}>
              {t("login.checkInboxTitle")}
            </Title>
            <Text className={subtitle} type="secondary">
              {t("login.checkInboxSubtitle")}{" "}
              <a className={sentEmphasis} href={`mailto:${sentToEmail}`}>
                {sentToEmail}
              </a>
            </Text>
            <Button block onClick={() => setSentToEmail(null)} size="large">
              {t("login.useDifferentEmail")}
            </Button>
          </>
        ) : (
          <>
            <Title className={titleStyle} level={3}>
              {t("login.title")}
            </Title>
            <Text className={subtitle} type="secondary">
              {t("login.subtitle")}
            </Text>

            <Text className={invitationNote}>{t("login.invitationOnlyNote")}</Text>

            <Form<FormValues> layout="vertical" onFinish={onFinish} requiredMark={false}>
              <Form.Item
                label={t("common.email")}
                name="email"
                rules={[
                  { required: true, message: t("login.validation.emailRequired") },
                  { type: "email", message: t("login.validation.emailInvalid") },
                ]}
              >
                <Input autoComplete="email" placeholder="you@example.com" size="large" />
              </Form.Item>

              {errorMessage ? <Alert className={alert} showIcon title={errorMessage} type="error" /> : null}

              <Form.Item className={submit}>
                <Button block htmlType="submit" icon={<MailOutlined />} loading={loading} size="large" type="primary">
                  {t("login.sendLink")}
                </Button>
              </Form.Item>

              <Text className={consentLine}>
                <Trans
                  components={{
                    privacyLink: <Link rel="noopener noreferrer" target="_blank" to={routes.privacy.to()} />,
                    strong: <strong />,
                    termsLink: <Link rel="noopener noreferrer" target="_blank" to={routes.terms.to()} />,
                  }}
                  i18nKey="login.consentLine"
                  values={{ button: t("login.sendLink") }}
                />
              </Text>
            </Form>
          </>
        )}
      </Card>

      <div className={langSwitcher}>
        <LanguageSwitcher />
      </div>
    </Flex>
  );
};
